// ตัดภาพเฉพาะแถวช่องฝนของข้อหนึ่งออกมาวางตั้งตรง — ให้ครูเห็นรอยฝนชัดๆ ทีละข้อ
// โดยไม่ต้องซูมหาในภาพทั้งแผ่น (ภาพย่อทั้งแผ่นกว้าง 720 px ช่องฝน 4.8 มม. เหลือแค่ ~16 px)
//
// ตัดจากภาพความละเอียดเต็ม (เฟรมกล้อง / หน้า PDF) ตอนอ่าน เก็บเป็น JPEG เล็กๆ เฉพาะข้อที่ต้องให้ครูดู
// พิกัดช่องฝน (rows[].bubbles) เป็นพิกัดภาพหลังผ่าน homography แล้ว แกนของแถวจึงหาจากช่องแรกไปช่องสุดท้าย —
// กระดาษที่หมุน/กลับหัวมาก็ออกมาตั้งตรงเอง เพราะช่องแรกคือ ก เสมอ

const MAX_CROPS = 40   // กันหน่วยความจำ: กระดาษที่อ่านเพี้ยนทั้งแผ่นไม่ต้องมีภาพครบทุกข้อ

// ภาพเทา { width, height, data } -> canvas (หน้า PDF ส่งมาเป็นภาพเทา)
export const grayToCanvas = (img) => {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  const out = ctx.createImageData(img.width, img.height)
  for (let i = 0, j = 0; i < img.data.length; i++, j += 4) {
    const v = img.data[i]
    out.data[j] = v
    out.data[j + 1] = v
    out.data[j + 2] = v
    out.data[j + 3] = 255
  }
  ctx.putImageData(out, 0, 0)
  return canvas
}

// แกนของภาพตัด = จาก points[0] ไป points[last] (ทิศ "ขวา" บนกระดาษ) กรอบครอบทุกจุด
// lead = เผื่อด้านหน้า (หน่วยรัศมีช่อง) ให้เห็นเลขข้อที่พิมพ์ไว้ซ้ายของช่องฝน
const crop = (src, points, { lead = 4, pad = 1.8 } = {}) => {
  if (!points?.length) return null
  const a = points[0]
  const b = points[points.length - 1]
  let ux = b.x - a.x
  let uy = b.y - a.y
  const len = Math.hypot(ux, uy)
  if (len < 1) { ux = 1; uy = 0 } else { ux /= len; uy /= len }
  const vx = -uy
  const vy = ux
  const r = points.reduce((s, p) => s + p.r, 0) / points.length
  const us = points.map((p) => (p.x - a.x) * ux + (p.y - a.y) * uy)
  const vs = points.map((p) => (p.x - a.x) * vx + (p.y - a.y) * vy)
  const minU = Math.min(...us) - r * lead
  const maxU = Math.max(...us) + r * pad
  const minV = Math.min(...vs) - r * pad
  const maxV = Math.max(...vs) + r * pad
  // ขยายให้ช่องฝนสูงราว 28 px — ภาพกล้องที่ย่อมาแล้วช่องเล็กเกินจะมองรอยจางไม่ออก
  const k = Math.min(3, Math.max(1, 14 / r))
  const w = Math.round((maxU - minU) * k)
  const h = Math.round((maxV - minV) * k)
  if (w < 4 || h < 4) return null
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const ctx = out.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, w, h)
  // พิกัดภาพต้นทาง s -> พิกัดภาพตัด: ((s - a)·u - minU) * k, ((s - a)·v - minV) * k
  ctx.setTransform(
    k * ux, k * vx, k * uy, k * vy,
    -k * (a.x * ux + a.y * uy) - k * minU,
    -k * (a.x * vx + a.y * vy) - k * minV,
  )
  ctx.drawImage(src, 0, 0)
  return { url: out.toDataURL('image/jpeg', 0.8), width: w, height: h }
}

// ภาพตัดของข้อที่ต้องให้ครูดู -> { [eq_no]: { url, width, height } }
export const cropRows = (src, rows, eqNos) => {
  const want = new Set([...eqNos].slice(0, MAX_CROPS))
  const out = {}
  for (const row of rows || []) {
    if (!want.has(row.eq_no)) continue
    const c = crop(src, row.bubbles)
    if (c) out[row.eq_no] = c
  }
  return out
}

// ช่องฝนเลขประจำตัวของกระดาษสำรองทั้งแผง — หลักหนึ่งคือคอลัมน์เลข 0-9 จากบนลงล่าง
// แกนจึงเป็นเลข 0 ของหลักแรกไปเลข 0 ของหลักสุดท้าย (ซ้ายไปขวาบนกระดาษ) ไม่ใช่ตามคอลัมน์ ไม่งั้นแผงออกมาตะแคง
export const cropIdDigits = (src, idRows) => {
  if (!idRows?.length) return null
  const all = idRows.flatMap((d) => d.bubbles)
  const first = idRows[0].bubbles[0]
  const last = idRows[idRows.length - 1].bubbles[0]
  return crop(src, [first, ...all, last], { lead: 1.8, pad: 1.8 })
}
